import { KYC_STATUS_ONBOARDING_NOT_FINISHED } from "@/hooks/useNeedsIDCheck";
import { portalRoutes } from "@/router/routes/portalRoutes";
import { IdentificationStatusResponseKycStatus } from "@/services/model";

const KYC_TODO_LABEL_PREFIX = "cockpit.dashboard.todos.kyc";

export type KycTodoVariant = "internal" | "external" | "informational";

export type KycTodoConfig = {
  labelKey: string;
  detailKey?: string;
  variant: KycTodoVariant;
  href?: string;
  hrefKey?: string;
};

const openTodo: KycTodoConfig = {
  labelKey: `${KYC_TODO_LABEL_PREFIX}.open`,
  variant: "internal",
  href: portalRoutes.cockpit.idCheck,
};

/** Statuses with no actionable ID-check page — dashboard shows info or external links only. */
export const NON_ACTIONABLE_ID_CHECK_STATUSES = [
  IdentificationStatusResponseKycStatus.PENDING,
  IdentificationStatusResponseKycStatus.FRAUD_CHECK_PENDING,
  IdentificationStatusResponseKycStatus.KYC_data_mismatch,
  IdentificationStatusResponseKycStatus.KYC_underaged,
  "KYC_max_attempts",
] as const;

export const isIdCheckPageActionable = (
  kycStatus: string | undefined,
): boolean => {
  if (!kycStatus) {
    return true;
  }

  return !NON_ACTIONABLE_ID_CHECK_STATUSES.includes(
    kycStatus as (typeof NON_ACTIONABLE_ID_CHECK_STATUSES)[number],
  );
};

export const getKycTodoConfig = (
  kycStatus: string | undefined,
): KycTodoConfig | null => {
  if (
    kycStatus === KYC_STATUS_ONBOARDING_NOT_FINISHED ||
    kycStatus === IdentificationStatusResponseKycStatus.OK ||
    kycStatus === undefined
  ) {
    return null;
  }

  switch (kycStatus) {
    case IdentificationStatusResponseKycStatus.OPEN:
      return openTodo;

    case IdentificationStatusResponseKycStatus.PENDING:
      return {
        labelKey: `${KYC_TODO_LABEL_PREFIX}.pending`,
        variant: "informational",
      };

    case IdentificationStatusResponseKycStatus.FRAUD_CHECK_PENDING:
      return {
        labelKey: `${KYC_TODO_LABEL_PREFIX}.fraudCheckPending`,
        variant: "informational",
      };

    case IdentificationStatusResponseKycStatus.KYC_data_mismatch:
      return {
        labelKey: `${KYC_TODO_LABEL_PREFIX}.dataMismatch`,
        variant: "internal",
        href: portalRoutes.user.personal,
      };

    case IdentificationStatusResponseKycStatus.KYC_underaged:
      return {
        labelKey: `${KYC_TODO_LABEL_PREFIX}.underaged`,
        variant: "external",
        hrefKey: "cockpit.kycSection.links.underaged",
      };

    case IdentificationStatusResponseKycStatus.KYC_invalid_document:
      return {
        labelKey: `${KYC_TODO_LABEL_PREFIX}.invalidDocument`,
        variant: "internal",
        href: portalRoutes.cockpit.idCheck,
      };

    case IdentificationStatusResponseKycStatus.KYC_ID_uncomparable:
      return {
        labelKey: `${KYC_TODO_LABEL_PREFIX}.idUncomparable`,
        variant: "internal",
        href: portalRoutes.cockpit.idCheck,
      };

    case IdentificationStatusResponseKycStatus.KYC_error_processing_docs:
      return {
        labelKey: `${KYC_TODO_LABEL_PREFIX}.errorProcessingDocs`,
        variant: "internal",
        href: portalRoutes.cockpit.idCheck,
      };

    case IdentificationStatusResponseKycStatus.KYC_error_processing_selfie:
      return {
        labelKey: `${KYC_TODO_LABEL_PREFIX}.errorProcessingSelfie`,
        variant: "internal",
        href: portalRoutes.cockpit.idCheck,
      };

    case "KYC_max_attempts":
      return {
        labelKey: `${KYC_TODO_LABEL_PREFIX}.maxAttempts`,
        variant: "external",
        hrefKey: "cockpit.kycSection.links.maxAttempts",
      };

    default:
      return openTodo;
  }
};
